import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Building2, User as UserIcon, Save } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { CUSTOMER_STATUSES } from "../lib/enums";
import VoiceTextarea from "../components/VoiceTextarea";

const empty = {
  customer_type: "individual",
  name: "",
  email: "",
  phone: "",
  whatsapp: "",
  google_place_id: "",
  google_rating: "",
  google_reviews_count: "",
  status: "active",
  notes: "",
  vat_number: "",
  registration_number: "",
  industry: "",
  website: "",
  billing_address: "",
  billing_city: "",
  billing_postal_code: "",
  billing_country: "",
  external_ref: "",
  allow_id_override: false,
  special_requests: "",
  internal_notes: "",
};

/* Wave 7-fix mobile polish — full-page replacement of the old "New customer"
   mini-modal. Same form, full viewport. */
export default function CustomerNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillName = params.get("name") || "";
  const [form, setForm] = useState({ ...empty, name: prefillName });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isCompany = form.customer_type === "company";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { ...form };
      payload.google_rating = payload.google_rating ? Number(payload.google_rating) : null;
      payload.google_reviews_count = payload.google_reviews_count
        ? Number(payload.google_reviews_count)
        : null;
      if (payload.customer_type === "individual") {
        [
          "vat_number",
          "registration_number",
          "industry",
          "website",
          "billing_address",
          "billing_city",
          "billing_postal_code",
          "billing_country",
        ].forEach((k) => (payload[k] = ""));
      }
      const { data } = await api.post("/customers", payload);
      navigate(`/customers/${data.id}`);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="customer-new-page">
      <div className="flex items-center gap-3">
        <Link to="/customers" className="hv-btn hv-btn-ghost" data-testid="customer-new-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Customers · new</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">New customer</h1>
        </div>
      </div>

      <form onSubmit={submit} className="hv-card p-5 md:p-6 space-y-4" data-testid="customer-form">
        {/* Type toggle */}
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Type</label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              { v: "individual", label: "Individual", icon: UserIcon, color: "#a78bfa" },
              { v: "company", label: "Company", icon: Building2, color: "#1fb6c1" },
            ].map((opt) => {
              const active = form.customer_type === opt.v;
              return (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setForm({ ...form, customer_type: opt.v })}
                  data-testid={`customer-type-${opt.v}`}
                  className={`flex items-center justify-center gap-2 py-3 rounded-md border transition ${
                    active
                      ? "border-[#1fb6c1] bg-[#1fb6c1]/10 text-white"
                      : "border-white/10 text-[#a0aabf] hover:border-white/30"
                  }`}
                  style={active ? { borderColor: opt.color, color: opt.color, background: `${opt.color}1a` } : {}}
                >
                  <opt.icon className="w-4 h-4" /> {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Common fields */}
        <div className="grid md:grid-cols-2 gap-3">
          <Field
            label={isCompany ? "Company name *" : "Full name *"}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            testid="customer-input-name"
          />
          <Field
            label="Email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            testid="customer-input-email"
          />
          <Field
            label="Phone (call)"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            testid="customer-input-phone"
          />
          <Field
            label="WhatsApp (intl. format)"
            value={form.whatsapp}
            onChange={(v) => setForm({ ...form, whatsapp: v })}
            testid="customer-input-whatsapp"
          />
        </div>

        {isCompany && (
          <div className="border border-[#1fb6c1]/20 rounded-md p-4 bg-[#1fb6c1]/5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-[#1fb6c1] flex items-center gap-2">
              <Building2 className="w-3 h-3" /> Company details
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="VAT / Tax number" value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} testid="customer-input-vat" />
              <Field label="Registration #" value={form.registration_number} onChange={(v) => setForm({ ...form, registration_number: v })} testid="customer-input-reg" />
              <Field label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />
              <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#1fb6c1] mt-3">
              Billing address (separate from sites)
            </div>
            <Field
              label="Street address"
              value={form.billing_address}
              onChange={(v) => setForm({ ...form, billing_address: v })}
              testid="customer-input-billing-address"
            />
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="City" value={form.billing_city} onChange={(v) => setForm({ ...form, billing_city: v })} />
              <Field label="Postal code" value={form.billing_postal_code} onChange={(v) => setForm({ ...form, billing_postal_code: v })} />
              <Field label="Country" value={form.billing_country} onChange={(v) => setForm({ ...form, billing_country: v })} />
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="hv-input mt-1"
              data-testid="customer-input-status"
            >
              {CUSTOMER_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <Field
            label="Google Place ID (optional)"
            value={form.google_place_id}
            onChange={(v) => setForm({ ...form, google_place_id: v })}
          />
        </div>

        <div className="border border-white/10 rounded-md p-4 bg-white/[0.02] space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] text-[#a0aabf]">Customer ID</div>
          <p className="text-[11px] text-[#647087] leading-relaxed">
            The customer's ID is generated automatically from the first 4
            letters of the name plus a 3-digit sequence (e.g.{" "}
            <span className="font-mono text-[#1fb6c1]">JOHN001</span>). IDs
            are permanent and cannot be edited — they're used as the
            shareable shorthand for this customer everywhere.
          </p>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Special requests (visible on Job Cards)</label>
            <VoiceTextarea
              value={form.special_requests}
              onChange={(v) => setForm({ ...form, special_requests: v })}
              rows={3}
              className="mt-1"
              context="lead_activity"
              testIdPrefix="customer-special-requests-voice"
              data-testid="customer-special-requests"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Internal notes (admin-only)</label>
            <VoiceTextarea
              value={form.internal_notes}
              onChange={(v) => setForm({ ...form, internal_notes: v })}
              rows={3}
              className="mt-1"
              context="lead_activity"
              testIdPrefix="customer-internal-notes-voice"
              data-testid="customer-internal-notes"
            />
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            rows={4}
            className="mt-1"
            context="lead_activity"
            testIdPrefix="customer-input-notes-voice"
            data-testid="customer-input-notes"
          />
        </div>

        {err && <div className="text-sm text-rose-300">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <Link to="/customers" className="hv-btn hv-btn-ghost">Cancel</Link>
          <button
            type="submit"
            disabled={busy}
            className="hv-btn hv-btn-primary"
            data-testid="customer-submit"
          >
            <Save className="w-4 h-4" /> {busy ? "Creating…" : "Create customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</label>
      <input
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="hv-input mt-1"
        data-testid={testid}
      />
    </div>
  );
}
